import Box from '@mui/material/Box';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Dialog, DialogTitle } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMemo } from 'react';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import { CameraConfiguration, CameraResolution, CameraVideocrop } from '../interfaces';

interface Props {
    open: boolean;
    cameraConfigurations: CameraConfiguration[];
    onClose: () => void;
}

const ResolutionCell = ({ value }: GridRenderCellParams<any, CameraResolution>) => {
    if (!value) return null;
    return (
        <span>
            {value.width} × {value.height}
        </span>
    );
};

const VideocropCell = ({ value }: GridRenderCellParams<any, CameraVideocrop>) => {
    if (!value) return null;
    return (
        <span>
            T:{value.top} B:{value.bottom} L:{value.left} R:{value.right}
        </span>
    );
};

const RotationCell = ({ value }: GridRenderCellParams<any, number[][]>) => {
    if (!value || !Array.isArray(value)) return null;
    return (
        <pre style={{ margin: 0, fontSize: '11px', lineHeight: 1.4 }}>
            {value.map((row) => `[${row.join(', ')}]`).join('\n')}
        </pre>
    );
};

export const CameraConfigurationsDialog = ({ cameraConfigurations, open, onClose }: Props) => {
    const columns: GridColDef[] = useMemo(() => {
        return [
            { key: 'name', width: 240 },
            { key: 'full_res', headerName: 'Full Res', width: 130, renderCell: ResolutionCell },
            { key: 'end_res', headerName: 'End Res', width: 130, renderCell: ResolutionCell },
            { key: 'videocrop', width: 210, renderCell: VideocropCell },
            { key: 'rotation', width: 160, renderCell: RotationCell },
        ].map((col) => ({
            field: col.key,
            headerName: col.headerName ?? transformKeyToTitle(col.key),
            width: col.width,
            renderCell: col.renderCell,
            align: 'left' as const,
            headerAlign: 'left' as const,
            sortable: false,
        }));
    }, []);

    return (
        <Dialog onClose={onClose} open={open} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
                Camera Configurations
                <CloseIcon sx={{ cursor: 'pointer' }} onClick={onClose} />
            </DialogTitle>
            <Box>
                <DataGrid
                    rows={cameraConfigurations}
                    rowHeight={80}
                    columns={columns}
                    getRowId={(row) => row.name}
                    disableRowSelectionOnClick
                    hideFooterPagination={true}
                />
            </Box>
        </Dialog>
    );
};
